import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Button from '../Button';
import { Section, Container, Badge } from '../shared/ui';

// Stundensätze/m²-Preise spiegeln die Angaben aus data/services.tsx (netto).
type CalcType = {
  label: string;
  rate: number;
  unit: 'hour' | 'sqm';
  productivity?: number; // m² pro Stunde (nur bei unit 'hour')
  recurring: boolean;
};

const TYPES: CalcType[] = [
  { label: 'Büroreinigung', rate: 31.5, unit: 'hour', productivity: 115, recurring: true },
  { label: 'Praxisreinigung', rate: 33.5, unit: 'hour', productivity: 90, recurring: true },
  { label: 'Treppenhausreinigung', rate: 31.5, unit: 'hour', productivity: 120, recurring: true },
  { label: 'Glas- und Fensterreinigung', rate: 41, unit: 'hour', productivity: 60, recurring: true },
  { label: 'Fitnessstudio-Reinigung', rate: 32.5, unit: 'hour', productivity: 90, recurring: true },
  { label: 'Kita- & Schulreinigung', rate: 31.5, unit: 'hour', productivity: 100, recurring: true },
  { label: 'Wohnungsreinigung', rate: 31.5, unit: 'hour', productivity: 70, recurring: true },
  { label: 'Bauschlussreinigung', rate: 35.5, unit: 'hour', productivity: 40, recurring: false },
  { label: 'Sonder- & Anlassreinigung', rate: 36.5, unit: 'hour', productivity: 60, recurring: false },
  { label: 'Grundreinigung', rate: 10.9, unit: 'sqm', recurring: false },
];

const INTERVALS = [
  { label: 'Wöchentlich', perWeek: true, perMonth: 0 },
  { label: '14-täglich', perWeek: false, perMonth: 2.17 },
  { label: 'Monatlich', perWeek: false, perMonth: 1 },
];

const fmt2 = (n: number) => n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtNum = (n: number) => n.toLocaleString('de-DE', { maximumFractionDigits: 2 });
const fmtEuro = (n: number) => n.toLocaleString('de-DE');

const Card = styled.div`
  max-width: 960px;
  margin: 0 auto;
  background: #fff;
  border: 1px solid #e3eaf5;
  border-radius: 24px;
  box-shadow: 0 18px 48px rgba(0, 52, 196, 0.1);
  overflow: hidden;
  display: grid;
  grid-template-columns: 1fr;
  @media (min-width: 820px) {
    grid-template-columns: 1.15fr 1fr;
  }
`;
const Controls = styled.div`
  padding: 2rem 1.8rem;
`;
const Field = styled.div`
  margin-bottom: 1.6rem;
`;
const FieldLabel = styled.label`
  display: block;
  font-weight: 700;
  color: #2b3652;
  margin-bottom: 0.6rem;
  font-size: 0.98rem;
`;
const Select = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #d5e0f2;
  border-radius: 12px;
  font-size: 1rem;
  color: #2b3652;
  background: #f8faff;
  cursor: pointer;
  &:focus {
    outline: none;
    border-color: var(--brand);
    box-shadow: 0 0 0 3px rgba(0, 52, 196, 0.12);
  }
`;
const RangeRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;
const NumberField = styled.div`
  display: inline-flex;
  align-items: baseline;
  gap: 0.35rem;
`;
const NumberInput = styled.input`
  width: 96px;
  padding: 0.35em 0.5em;
  border: 1px solid #d5e0f2;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 800;
  color: var(--brand);
  text-align: right;
  &:focus {
    outline: none;
    border-color: var(--brand);
    box-shadow: 0 0 0 3px rgba(0, 52, 196, 0.12);
  }
`;
const NumberUnit = styled.span`
  font-weight: 800;
  color: var(--brand);
`;
const Range = styled.input`
  width: 100%;
  accent-color: var(--brand);
  cursor: pointer;
`;
const RangeHint = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.82rem;
  /* #8593ad kam auf Weiss nur auf 3,1:1 - WCAG AA verlangt 4,5:1 fuer
     Fliesstext dieser Groesse. */
  color: var(--ink-soft);
  margin-top: 0.25rem;
`;
const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;
const Chip = styled.button<{ $active: boolean }>`
  border: 1px solid ${p => (p.$active ? 'var(--brand)' : '#d5e0f2')};
  background: ${p => (p.$active ? 'var(--brand)' : '#f8faff')};
  color: ${p => (p.$active ? '#fff' : '#2b3652')};
  font-weight: 600;
  font-size: 0.92rem;
  border-radius: 999px;
  /* 44px ist die uebliche Mindestgroesse fuer Touch-Bedienung; vorher waren
     die Chips 30px hoch und auf dem Handy schwer zu treffen. */
  min-height: 44px;
  min-width: 44px;
  padding: 0.5em 1.05em;
  cursor: pointer;
  transition: all 0.2s;
`;
const OnceNote = styled.div`
  font-size: 0.92rem;
  color: #3a4662;
  background: #f1f5ff;
  border: 1px solid #d5e0f2;
  border-radius: 12px;
  padding: 0.75rem 1rem;
`;
const Result = styled.div`
  background: linear-gradient(135deg, var(--brand) 0%, #234be6 55%, #6f8df5 100%);
  color: #fff;
  padding: 2rem 1.8rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
`;
const ResultLabel = styled.div`
  font-size: 0.95rem;
  opacity: 0.9;
`;
const ResultValue = styled.div`
  font-size: 2.6rem;
  font-weight: 800;
  line-height: 1.1;
  margin: 0.4rem 0;
`;
const ResultPeriod = styled.div`
  font-size: 0.9rem;
  opacity: 0.9;
  margin-bottom: 1rem;
`;
const ResultFormula = styled.div`
  font-size: 0.85rem;
  opacity: 0.85;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 12px;
  padding: 0.6rem 0.8rem;
  margin-bottom: 1.4rem;
`;
const ResultNote = styled.div`
  font-size: 0.78rem;
  opacity: 0.85;
  margin-top: 1rem;
`;
const Head = styled.div`
  text-align: center;
  margin-bottom: 2.2rem;
`;
const Title = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: var(--brand);
  margin: 0.75rem 0 0.5rem;
`;
const Sub = styled.p`
  max-width: 640px;
  margin: 0 auto;
  color: #3a4662;
  line-height: 1.6;
`;

const Preisrechner: React.FC = () => {
  const [typeIndex, setTypeIndex] = React.useState(0);
  const [area, setArea] = React.useState(80);
  const [freqIndex, setFreqIndex] = React.useState(0);
  const [daysPerWeek, setDaysPerWeek] = React.useState(2);
  const navigate = useNavigate();

  const t = TYPES[typeIndex];

  let cost = 0;
  let formula = '';
  let period = '';
  if (t.unit === 'sqm') {
    cost = area * t.rate;
    formula = `${area} m² × ${fmt2(t.rate)} €/m²`;
    period = 'einmalig, netto';
  } else {
    const hours = Math.max(area / (t.productivity as number), 0.5);
    if (t.recurring) {
      const interval = INTERVALS[freqIndex];
      const einsaetze = interval.perWeek ? daysPerWeek * 4.33 : interval.perMonth;
      cost = hours * t.rate * einsaetze;
      formula = `${fmtNum(hours)} Std. × ${fmt2(t.rate)} €/Std. × ${fmtNum(einsaetze)} Einsätze/Monat`;
      period = 'monatlich, netto';
    } else {
      cost = hours * t.rate;
      formula = `${fmtNum(hours)} Std. × ${fmt2(t.rate)} €/Std.`;
      period = 'einmalig, netto';
    }
  }
  const rounded = Math.round(cost / 10) * 10;

  let haeufigkeitText = 'Einmalig / nach Bedarf';
  if (t.recurring) {
    const interval = INTERVALS[freqIndex];
    haeufigkeitText = interval.perWeek ? `${daysPerWeek} ${daysPerWeek === 1 ? 'Tag' : 'Tage'}/Woche` : interval.label;
  }

  const goToKontakt = () => {
    const params = new URLSearchParams({
      leistung: t.label,
      flaeche: String(area),
      haeufigkeit: haeufigkeitText,
      preis: `ca. ${fmtEuro(rounded)} € (${period})`,
    });
    navigate(`/kontakt?${params.toString()}`);
  };

  return (
    <Section>
      <Container>
        <Head>
          <Badge>Preisrechner</Badge>
          <Title>Was kostet Ihre Reinigung?</Title>
          <Sub>
            Stellen Sie Reinigungsart, Fläche und Häufigkeit ein und erhalten Sie eine unverbindliche Kostenschätzung. Den verbindlichen Festpreis erstellen wir nach einer kostenlosen Besichtigung.
          </Sub>
        </Head>
        <Card>
          <Controls>
            <Field>
              <FieldLabel htmlFor="pr-type">Reinigungsart</FieldLabel>
              <Select
                id="pr-type"
                value={typeIndex}
                onChange={(e) => setTypeIndex(Number(e.target.value))}
              >
                {TYPES.map((ct, i) => (
                  <option key={ct.label} value={i}>{ct.label}</option>
                ))}
              </Select>
            </Field>
            <Field>
              <RangeRow>
                <FieldLabel as="span">Fläche</FieldLabel>
                <NumberField>
                  <NumberInput
                    type="number"
                    min={20}
                    max={1000}
                    step={10}
                    value={area}
                    aria-label="Fläche in Quadratmetern"
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (Number.isFinite(v)) setArea(Math.min(v, 1000));
                    }}
                    onBlur={(e) => {
                      const v = Number(e.target.value);
                      setArea(Number.isFinite(v) ? Math.min(Math.max(v, 20), 1000) : 80);
                    }}
                  />
                  <NumberUnit>m²</NumberUnit>
                </NumberField>
              </RangeRow>
              <Range
                type="range"
                min={20}
                max={1000}
                step={10}
                value={area}
                aria-label="Fläche in Quadratmetern (Schieberegler)"
                onChange={(e) => setArea(Number(e.target.value))}
              />
              <RangeHint>
                <span>20 m²</span>
                <span>1.000 m²</span>
              </RangeHint>
            </Field>
            {t.recurring ? (
              <>
                <Field>
                  <FieldLabel as="span">Häufigkeit</FieldLabel>
                  <Chips>
                    {INTERVALS.map((f, i) => (
                      <Chip
                        key={f.label}
                        type="button"
                        $active={i === freqIndex}
                        onClick={() => setFreqIndex(i)}
                      >
                        {f.label}
                      </Chip>
                    ))}
                  </Chips>
                </Field>
                {INTERVALS[freqIndex].perWeek && (
                  <Field style={{ marginBottom: 0 }}>
                    <FieldLabel as="span">Reinigungstage pro Woche</FieldLabel>
                    <Chips>
                      {[1, 2, 3, 4, 5, 6, 7].map((d) => (
                        <Chip
                          key={d}
                          type="button"
                          $active={d === daysPerWeek}
                          onClick={() => setDaysPerWeek(d)}
                        >
                          {d}
                        </Chip>
                      ))}
                    </Chips>
                  </Field>
                )}
              </>
            ) : (
              <Field style={{ marginBottom: 0 }}>
                <FieldLabel as="span">Häufigkeit</FieldLabel>
                <OnceNote>Diese Leistung wird einmalig bzw. nach Bedarf ausgeführt.</OnceNote>
              </Field>
            )}
          </Controls>
          <Result>
            <ResultLabel>Geschätzte Kosten</ResultLabel>
            <ResultValue>ca. {fmtEuro(rounded)} €</ResultValue>
            <ResultPeriod>{period}</ResultPeriod>
            <ResultFormula>≈ {formula}</ResultFormula>
            <Button size="lg" type="button" style={{ width: '100%' }} onClick={goToKontakt}>Unverbindliches Angebot anfordern</Button>
            <ResultNote>Unverbindliche Schätzung. Alle Preise zzgl. MwSt.</ResultNote>
          </Result>
        </Card>
      </Container>
    </Section>
  );
};

export default Preisrechner;
